import { configureStore, combineReducers } from '@reduxjs/toolkit'
import {
  useDispatch,
  useSelector,
  type TypedUseSelectorHook,
} from 'react-redux'
import storage from 'redux-persist/lib/storage/session'
import {
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  persistReducer,
  createTransform,
} from 'redux-persist'
import {
  patientRegistrationApi,
  patientRegistrationExternalReducer,
  patientAuthMiddleware,
  PATIENT_AUTH_REDUCER_KEY,
  referenceDataApi,
  patientAuthReducerPath,
  patientAuthReducer,
  patientAuthApi,
  availabilityExternalApi,
  schedulingExternalApi,
  verifyApiExternal,
  configExternalApi,
  formsApi,
} from '@valerahealth/rtk-query'
import { notificationsReducer } from '@valerahealth/ui-components/features'
import { type PatientAuthState } from '@valerahealth/rtk-query'
import { patientRegistrationExternalEnhancedApi } from 'api/patientRegistrationExternalApiServiceEnhanced'
import app from './appSlice'
import {
  appMiddleware,
  insuranceVerificiationMiddleware,
} from './appMiddleware'

// check if we are still authenticated when rehydrating state
export const authTransform = createTransform(
  null,
  (state: PatientAuthState): PatientAuthState => {
    const { session } = state
    const { exp } = session || {}
    return {
      ...state,
      isAuthenticated: !!exp && exp * 1000 > Date.now(),
    }
  },
  { whitelist: [PATIENT_AUTH_REDUCER_KEY] },
)

const rootReducer = combineReducers({
  [app.name]: app.reducer,
  [patientRegistrationApi.reducerPath]: patientRegistrationApi.reducer,
  [referenceDataApi.reducerPath]: referenceDataApi.reducer,
  [patientAuthApi.reducerPath]: patientAuthApi.reducer,
  [patientAuthReducerPath]: patientAuthReducer,
  [availabilityExternalApi.reducerPath]: availabilityExternalApi.reducer,
  [schedulingExternalApi.reducerPath]: schedulingExternalApi.reducer,
  [verifyApiExternal.reducerPath]: verifyApiExternal.reducer,
  [configExternalApi.reducerPath]: configExternalApi.reducer,
  [formsApi.reducerPath]: formsApi.reducer,
  ...patientRegistrationExternalReducer,
  ...notificationsReducer,
})

const persistedReducer = persistReducer(
  {
    key: 'root',
    storage,
    blacklist: [
      // patientRegistrationApi.reducerPath,
      //referenceDataApi.reducerPath,
      // we want this to persist because we need the submission id. If someone refreshes the page we want to get the submission id back
      //patientRegistrationExternalEnhancedApi.reducerPath,
      ...Object.keys(notificationsReducer),
      schedulingExternalApi.reducerPath,
    ],
    transforms: [authTransform],
  },
  rootReducer,
) as typeof rootReducer

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(
      patientRegistrationApi.middleware,
      patientRegistrationExternalEnhancedApi.middleware,
      schedulingExternalApi.middleware,
      referenceDataApi.middleware,
      patientAuthApi.middleware,
      availabilityExternalApi.middleware,
      verifyApiExternal.middleware,
      configExternalApi.middleware,
      formsApi.middleware,
      patientAuthMiddleware,
      appMiddleware,
      insuranceVerificiationMiddleware,
    ),
})
export const persistor = persistStore(store)
export const { getState } = store

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

export const useReduxDispatch = () => useDispatch<AppDispatch>()
export const useReduxSelector: TypedUseSelectorHook<RootState> = useSelector
